import { InputField } from 'fields/base/InputField/InputField';
import { DurationField } from 'fields/DurationField';
import { FormikErrors, FormikTouched } from 'formik';

import { ArbitrageStableCheckingMethod, IArbitrageGeneralSettings } from 'types/arbitrage';
import { FilterSelectField } from 'ui/Filters/fields/SelectField';
import { Switcher } from 'ui/Switcher/Switcher';

const STABLE_CHECKING_METHOD_OPTIONS = [
  ArbitrageStableCheckingMethod.check_default,
  ArbitrageStableCheckingMethod.check_by_average_price,
  ArbitrageStableCheckingMethod.check_by_liquidity,
  ArbitrageStableCheckingMethod.check_by_update_time,
];

const stableCheckingMethodLabels: Record<ArbitrageStableCheckingMethod, string> = {
  [ArbitrageStableCheckingMethod.check_default]: 'Default',
  [ArbitrageStableCheckingMethod.check_by_average_price]: 'By Average Price',
  [ArbitrageStableCheckingMethod.check_by_liquidity]: 'By Liquidity',
  [ArbitrageStableCheckingMethod.check_by_update_time]: 'By Update Time',
};

interface Props {
  values: IArbitrageGeneralSettings;
  setFieldValue: (v: keyof IArbitrageGeneralSettings, va: any) => any;
  touched: FormikTouched<IArbitrageGeneralSettings>;
  errors: FormikErrors<IArbitrageGeneralSettings>;
  setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void;
}

const GeneralSettingsForm = ({
  values,
  setFieldValue,
  touched,
  errors,
  setFieldTouched,
}: Props) => {
  return (
    <form className="w-full flex flex-col gap-5">
      {/* Row 1: Min profit & USD threshold */}
      <div className="grid grid-cols-2 gap-6">
        <InputField
          label="Min profit (%)"
          type="decimal-number"
          value={values.min_profit}
          setValue={v => {
            setFieldTouched('min_profit', true, true);
            setFieldValue('min_profit', v);
          }}
          errorMessage={touched.min_profit ? errors.min_profit : undefined}
        />
        <InputField
          label="USD threshold"
          type="decimal-number"
          value={values.usd_threshold}
          setValue={v => {
            setFieldTouched('usd_threshold', true, true);
            setFieldValue('usd_threshold', v);
          }}
          errorMessage={touched.usd_threshold ? errors.usd_threshold : undefined}
        />
      </div>

      {/* Row 2: Maximum limit & Cancel timeout */}
      <div className="grid grid-cols-2 gap-6">
        <InputField
          label="Maximum limit (USD)"
          type="decimal-number"
          value={values.maximum_limit_usd}
          setValue={v => {
            setFieldTouched('maximum_limit_usd', true, true);
            setFieldValue('maximum_limit_usd', v);
          }}
          errorMessage={touched.maximum_limit_usd ? errors.maximum_limit_usd : undefined}
        />
        <DurationField
          label="Default cancel timeout"
          value={values.default_cancel_timeout_sec}
          setValue={v => {
            setFieldTouched('default_cancel_timeout_sec', true, true);
            setFieldValue('default_cancel_timeout_sec', v);
          }}
          errorMessage={
            touched.default_cancel_timeout_sec ? errors.default_cancel_timeout_sec : undefined
          }
          zeroable
        />
      </div>

      {/* Row 3: Maximum limited toggle, Close Position Automatically & Stable Checking Method */}
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-3 text-sm font-medium text-gray-1 whitespace-nowrap">
          Maximum limited
          <Switcher
            value={values.is_maximum_limited}
            onChange={v => {
              setFieldTouched('is_maximum_limited', true, true);
              setFieldValue('is_maximum_limited', v);
            }}
          />
        </label>

        <label className="flex items-center gap-3 text-sm font-medium text-gray-1 whitespace-nowrap">
          Close Position Automatically
          <Switcher
            value={values.close_position_automatically}
            onChange={v => {
              setFieldTouched('close_position_automatically', true, true);
              setFieldValue('close_position_automatically', v);
            }}
          />
        </label>

        <div className="flex-1 flex flex-col gap-1">
          <span id="stable-checking-method-label" className="text-xs font-medium text-gray-1">
            Stable Checking Method
          </span>
          <FilterSelectField
            aria-labelledby="stable-checking-method-label"
            items={STABLE_CHECKING_METHOD_OPTIONS}
            selected={values.stable_checking_method}
            onSelectItem={v => {
              setFieldTouched('stable_checking_method', true, true);
              setFieldValue('stable_checking_method', v);
            }}
            itemToString={item => stableCheckingMethodLabels[item]}
            placeholder="Select method"
          />
          {touched.stable_checking_method && errors.stable_checking_method && (
            <span className="text-red-1 text-xs">{errors.stable_checking_method}</span>
          )}
        </div>
      </div>
    </form>
  );
};

export { GeneralSettingsForm };
